/* ═══════════════════════════ time, and how it reads ═══════════════════════

   Dates, durations, and the formatting of both. Every function here is pure:
   given the same arguments it returns the same answer, it touches no state,
   and it names no part of the DOM. That is what makes it the bottom of the
   dependency order — everything may import this, and it imports nothing.

   The one thing worth reading before editing: nothing here does date
   arithmetic by adding milliseconds. See `addDays`. */

/** Local timezone, resolved once. Only ever displayed. */
export const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export const DAY_MS = 86400000;

/** A random-ish id for a thing the user just made.
 *
 *  Deliberately absent from the planner: every id the engine mints is derived
 *  from state, because `S.weekly.moved` and `S.weekly.misses` are keyed by
 *  block id and a fresh id on every recompute would orphan both. This one is
 *  for rows the user creates by hand, which are saved the moment they exist. */
export const uid = () =>
  Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);

export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export const pad = (n) => String(n).padStart(2, '0');

/* ─────────────────────────── days ─────────────────────────── */

export const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/* Adding n × DAY_MS to a midnight lands an hour either side of the next one
   whenever the clocks have changed in between, and a day boundary an hour off
   its day is a boundary that can repeat itself: on the long Sunday in autumn,
   midnight plus twenty-four hours is still that same Sunday, so a loop stepping
   with it never leaves. Asking the calendar for the next date has no such day. */
export const addDays = (d, n) => { const x = startOfDay(d); x.setDate(x.getDate() + n); return x; };

/** Weeks run Monday → Sunday. */
export function startOfWeek(d) {
  const x = startOfDay(d);
  return addDays(x, -((x.getDay() + 6) % 7));
}

export const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

/** The same as `startOfDay`, already in milliseconds. */
export const dayStart = (ms) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };

export const isToday = (d) => sameDay(d, new Date());

/** Sunday-first, matching `Date.getDay()`. The week grid is Monday-first and
 *  indexes this through `dowIndex`; do not reorder it to match the grid. */
export const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Monday-first index, 0…6 — the order the week grid draws in. */
export const dowIndex = (d) => (new Date(d).getDay() + 6) % 7;

export const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

// NB: the calendar owns an isToday(Date); this one takes a timestamp.
export const createdToday = (ts) => typeof ts === 'number' && ts >= startOfToday();

/* ─────────────────────── days as strings ─────────────────────── */

/** Local YYYY-MM-DD. Both ends of the planner speak in these, so they must agree. */
export const isoDay = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Parse one back. Bare dates are read as local midnight, never as UTC — an
 *  hour's drift here moves a deadline to the day before. */
export function fromIsoDay(s) {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) { const t = new Date(s).getTime(); return Number.isFinite(t) ? t : null; }
  return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
}

export const fmtDay = (ms) =>
  new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/* ─────────────────────── minutes of the day ─────────────────────── */

export const minToHhmm = (n) =>
  `${pad(Math.floor(clamp(n, 0, 1440) / 60))}:${pad(Math.round(n) % 60)}`;

/** "14:30" → 870, or null if it is not a time. The inverse of the above, and
 *  the pair is why both live here rather than beside whoever parses a form. */
export function hhmmToMin(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || ''));
  return m ? clamp(+m[1] * 60 + +m[2], 0, 1440) : null;
}

/* ─────────────────────── durations, as read ─────────────────────── */

export function fmtDurMin(min) {
  min = Math.round(min);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** The same duration in words, for prose rather than a chip. */
export function fmtHours(min) {
  const h = min / 60;
  if (h < 1) {
    const m = Math.round(min);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  const r = Math.round(h * 10) / 10;
  return `${r} hour${r === 1 ? '' : 's'}`;
}

/** Seconds as a running clock: 4:03, or 1:04:03 once it passes an hour. */
export function fmtClock(sec) {
  sec = Math.max(0, Math.floor(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/* ─────────────────────── moments, as read and typed ─────────────────────── */

/** The value a <input type="datetime-local"> wants. */
export function toLocalInput(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export function fromLocalInput(v) { return v ? new Date(v).getTime() : null; }

export function fmtWhen(ms) {
  const d = new Date(ms);
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Google Calendar / iCal basic local-time stamp: 20260805T143000 */
export function stampLocal(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
}

export function stampUTC(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

/* ─────────────────────── text ─────────────────────── */

export function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
